// src/helpers/searchTrend.ts
import { differenceInCalendarDays, format, parseISO } from "date-fns";
import {
  AnalyticsGranularity,
  SearchPreset,
  SearchSeriesPoint,
  SearchBreakdown,
  SearchSegmentSeries,
} from "../api/analyticsAdmin";
import { segmentLabel } from "./searchAnalytics";
import { sortSegments } from "./analyticsPlus";

export const autoGranularity = (
  from: string,
  to: string
): AnalyticsGranularity => {
  const span = Math.abs(
    differenceInCalendarDays(parseISO(to), parseISO(from))
  );
  if (span <= 31) return "Day";
  if (span <= 120) return "Week";
  return "Month";
};

// Fixed presets (Last3/7/30Days) are all <= 31 days, so Day is always the
// sensible default; only Custom needs the span-based rule.
export const autoGranularityForFilters = (f: {
  preset: SearchPreset;
  from: string;
  to: string;
}): AnalyticsGranularity =>
  f.preset === "Custom" ? autoGranularity(f.from, f.to) : "Day";

export const pointSearchToOpenRate = (p: SearchSeriesPoint): number | null =>
  p.searches > 0 ? p.searchesWithOpen / p.searches : null;

export const pointSearchToOutboundRate = (
  p: SearchSeriesPoint
): number | null =>
  p.searches > 0 ? p.searchesWithOutbound / p.searches : null;

export const formatBucketTick = (
  bucketStart: string,
  granularity: AnalyticsGranularity
): string =>
  format(parseISO(bucketStart), granularity === "Month" ? "MMM yyyy" : "d MMM");

export type TrendMode = "Volume" | "Conversion";
export type VolumeMetric =
  | "searches"
  | "opens"
  | "outbounds"
  | "uniqueSearchers";
export type RateMetric = "searchToOpenRate" | "searchToOutboundRate";

export interface TrendRow {
  bucketStart: string;
  [key: string]: number | string | null;
}

export interface TrendSeriesDef {
  key: string;
  label: string;
  color: string;
}

export interface TrendChart {
  rows: TrendRow[];
  series: TrendSeriesDef[];
  stacked: boolean;
  valueKind: "count" | "rate";
}

export interface BuildTrendArgs {
  series: SearchSegmentSeries[];
  mode: TrendMode;
  breakdown: SearchBreakdown;
  volumeMetric: VolumeMetric;
  rateMetric: RateMetric;
}

export const SEGMENT_COLORS = [
  "#503595",
  "#8b5cf6",
  "#0ea5e9",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#ec4899",
  "#14b8a6",
];

// Fixed funnel-stage lines for the un-split (breakdown=None) Volume view.
const NONE_VOLUME_SERIES: { key: VolumeMetric; label: string; color: string }[] = [
  { key: "searches", label: "Searches", color: "#503595" },
  { key: "uniqueSearchers", label: "Searchers", color: "#8b5cf6" },
  { key: "opens", label: "Opens", color: "#0ea5e9" },
  { key: "outbounds", label: "Outbound", color: "#10b981" },
];

const NONE_RATE_SERIES: { key: RateMetric; label: string; color: string }[] = [
  { key: "searchToOpenRate", label: "Search → open", color: "#503595" },
  { key: "searchToOutboundRate", label: "Search → outbound", color: "#8b5cf6" },
];

const byBucketStart = (a: { bucketStart: string }, b: { bucketStart: string }) =>
  a.bucketStart < b.bucketStart ? -1 : a.bucketStart > b.bucketStart ? 1 : 0;

const bucketUnion = (series: SearchSegmentSeries[]): string[] => {
  const set = new Set<string>();
  series.forEach((s) => s.points.forEach((p) => set.add(p.bucketStart)));
  return Array.from(set).sort();
};

const rateOf = (p: SearchSeriesPoint, metric: RateMetric): number | null =>
  metric === "searchToOpenRate"
    ? pointSearchToOpenRate(p)
    : pointSearchToOutboundRate(p);

const noneSeries = (series: SearchSegmentSeries[]): SearchSeriesPoint[] => {
  const all = series.find((s) => s.segment === "all") ?? series[0];
  return all ? [...all.points].sort(byBucketStart) : [];
};

const buildSegment = (args: BuildTrendArgs): TrendChart => {
  // Deterministic legend/color order: age bands chronological, other
  // breakdowns by total volume, "unknown" last (see sortSegments).
  const ordered = sortSegments(
    args.breakdown,
    args.series,
    (s) => s.segment,
    (s) => s.points.reduce((sum, p) => sum + p.searches, 0)
  );
  const buckets = bucketUnion(ordered);
  const series: TrendSeriesDef[] = ordered.map((s, i) => ({
    key: s.segment,
    label: segmentLabel(s.segment),
    color: SEGMENT_COLORS[i % SEGMENT_COLORS.length],
  }));
  // bucketStart -> segment -> point, for O(1) lookup while filling the grid.
  const index = new Map<string, Map<string, SearchSeriesPoint>>();
  ordered.forEach((s) => {
    const inner = new Map<string, SearchSeriesPoint>();
    s.points.forEach((p) => inner.set(p.bucketStart, p));
    index.set(s.segment, inner);
  });

  const isVolume = args.mode === "Volume";
  const rows: TrendRow[] = buckets.map((bucketStart) => {
    const row: TrendRow = { bucketStart };
    ordered.forEach((s) => {
      const p = index.get(s.segment)?.get(bucketStart);
      if (isVolume) {
        row[s.segment] = p ? p[args.volumeMetric] : 0;
      } else {
        row[s.segment] = p ? rateOf(p, args.rateMetric) : null;
      }
    });
    return row;
  });

  return {
    rows,
    series,
    stacked: isVolume,
    valueKind: isVolume ? "count" : "rate",
  };
};

const buildNone = (args: BuildTrendArgs): TrendChart => {
  const points = noneSeries(args.series);
  if (args.mode === "Volume") {
    return {
      stacked: false,
      valueKind: "count",
      series: NONE_VOLUME_SERIES,
      rows: points.map((p) => ({
        bucketStart: p.bucketStart,
        searches: p.searches,
        uniqueSearchers: p.uniqueSearchers,
        opens: p.opens,
        outbounds: p.outbounds,
      })),
    };
  }
  return {
    stacked: false,
    valueKind: "rate",
    series: NONE_RATE_SERIES,
    rows: points.map((p) => ({
      bucketStart: p.bucketStart,
      searchToOpenRate: pointSearchToOpenRate(p),
      searchToOutboundRate: pointSearchToOutboundRate(p),
    })),
  };
};

export const buildTrend = (args: BuildTrendArgs): TrendChart =>
  args.breakdown === "None" ? buildNone(args) : buildSegment(args);
